import { create } from "zustand";

interface SyncState {
  isOnline: boolean;
  isSyncing: boolean;
  lastSyncAt: Date | null;
  pendingChanges: number;
  failedCount: number;
  setSyncing: (syncing: boolean) => void;
  setLastSync: (date: Date) => void;
  setOnline: (online: boolean) => void;
  setPendingCount: (count: number) => void;
  setFailedCount: (count: number) => void;
  incrementPending: () => void;
  decrementPending: () => void;
  resetPending: () => void;
  triggerSync: () => Promise<void>;
  retryFailed: () => Promise<void>;
  clearFailed: () => Promise<void>;
  initializeSync: () => () => void;
}

const onlineOnlyError = () =>
  new Error(
    "MediCorePay Web has no offline mutation queue. Reconnect and retry the server command.",
  );

export const useSyncStore = create<SyncState>((set) => ({
  isOnline: navigator.onLine,
  isSyncing: false,
  lastSyncAt: null,
  pendingChanges: 0,
  failedCount: 0,
  setSyncing: (isSyncing) => set({ isSyncing }),
  setLastSync: (lastSyncAt) => set({ lastSyncAt }),
  setOnline: (isOnline) => set({ isOnline }),
  setPendingCount: () => set({ pendingChanges: 0 }),
  setFailedCount: () => set({ failedCount: 0 }),
  incrementPending: () => {
    throw onlineOnlyError();
  },
  decrementPending: () => set({ pendingChanges: 0 }),
  resetPending: () => set({ pendingChanges: 0 }),
  triggerSync: async () => {
    if (!navigator.onLine) throw onlineOnlyError();
    set({ lastSyncAt: new Date(), pendingChanges: 0, failedCount: 0 });
  },
  retryFailed: async () => {
    if (!navigator.onLine) throw onlineOnlyError();
    set({ pendingChanges: 0, failedCount: 0 });
  },
  clearFailed: async () => set({ pendingChanges: 0, failedCount: 0 }),
  initializeSync: () => {
    const update = () =>
      set({
        isOnline: navigator.onLine,
        isSyncing: false,
        pendingChanges: 0,
        failedCount: 0,
      });
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    update();
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  },
}));
